import { useLCPDetection } from "@/hooks/use-lcp-detection";
import { cn } from "@/utils/cn";
import Image from "next/image";
import React, { useEffect, useState } from "react";

const slides = [
	{
		img: "/images/home/hero/home_list_search.png",
		alt: "Search/List",
		label: "Search/List",
		width: 790 / 3,
		height: 702 / 3,
	},
	{
		img: "/images/home/hero/home_visit.png",
		alt: "Visit",
		label: "Visit",
		width: 579 / 2,
		height: 563 / 2,
	},
	{
		img: "/images/home/hero/legal_loan.png",
		alt: "Legal & Loan",
		label: "Legal & Loan",
		width: 572 / 2,
		height: 689 / 2,
	},
	{
		img: "/images/home/hero/home_settled.png",
		alt: "Settled",
		label: "✔︎ Settled",
		width: 671 / 2,
		height: 676 / 2,
	},
];

export const HeroCarousel: React.FC = () => {
	const [currentSlide, setCurrentSlide] = useState(0);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [hovered, setHovered] = useState(false);

	const lcpCompleted = useLCPDetection();

	useEffect(() => {
		if (!lcpCompleted) return;

		// Disable initial animation for LCP optimization
		const initialTimer = setTimeout(() => setIsInitialLoad(false), 100);

		// Start auto-slide after LCP
		const timer = setInterval(() => {
			setCurrentSlide(prev => (prev + 1) % slides.length);
			setHovered(false);
		}, 3000);

		return () => {
			clearInterval(timer);
			clearTimeout(initialTimer);
		};
	}, [lcpCompleted]);

	const shouldAnimate = lcpCompleted && !isInitialLoad;

	return (
		<div className="relative w-full h-[400px] md:h-[450px] lg:h-[500px] transform-gpu">
			{slides.map((slide, index) => (
				<div
					key={slide.label}
					className={cn(
						"absolute inset-0 transform transform-gpu",
						!lcpCompleted && index === 0
							? "opacity-100 translate-x-0 z-20"
							: !lcpCompleted
								? "opacity-0 translate-x-full z-10"
								: index === currentSlide
									? "opacity-100 translate-x-0 z-20"
									: index < currentSlide
										? "opacity-0 -translate-x-full z-10"
										: "opacity-0 translate-x-full z-10",
					)}
					onMouseEnter={() => setHovered(true)}
					onMouseLeave={() => setHovered(false)}
					style={{
						transition: shouldAnimate ? "all 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)" : "none",
						willChange: shouldAnimate && index === currentSlide ? "transform, opacity" : "auto",
					}}
				>
					<div className="flex items-center md:items-end justify-center md:justify-end h-full">
						<div
							className={cn(
								"group/card relative isolate transform-gpu",
								"flex flex-col items-center justify-center",
								"w-[90%] h-[350px] md:h-[400px] lg:h-[450px]",
								"rounded-3xl",
								"border-2 border-accent/30",
								"bg-gradient-to-br from-background via-background/95 to-accent/10",
								"overflow-hidden",
								"hover:scale-[1.02] hover:border-accent",
								"before:absolute before:inset-0 before:bg-gradient-to-br before:from-accent/5 before:via-accent/10 before:to-accent/20",
								"before:opacity-0",
								"hover:before:opacity-100",
								"transition-all duration-400",
							)}
							style={{
								transition: shouldAnimate ? "all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)" : "none",
							}}
						>
							{/* Animated gradient background */}
							<div
								className="absolute inset-0 -z-10 bg-[size:200%_200%] opacity-30 transform-gpu"
								style={{
									backgroundImage:
										"linear-gradient(45deg, oklch(var(--accent) / 0.2), oklch(var(--primary) / 0.1), oklch(var(--accent) / 0.2))",
									animation: shouldAnimate
										? "gradient-shift 12s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite"
										: "none",
									willChange: shouldAnimate ? "background-position" : "auto",
								}}
							/>

							<div className="relative z-10 flex flex-col items-center gap-8">
								<div
									className="relative w-[150px] md:w-[180px] lg:w-[200px] aspect-square transform-gpu transition-transform duration-400"
									style={{
										transition: shouldAnimate ? "transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)" : "none",
										transform: shouldAnimate && hovered && index === currentSlide ? "scale(1.10) rotate(3deg)" : "none",
									}}
								>
									<Image
										src={slide.img}
										alt={slide.alt}
										width={slide.width}
										height={slide.height}
										className="object-contain drop-shadow-lg group-hover/card:drop-shadow-2xl transition-all duration-400"
										style={{
											transition: shouldAnimate ? "filter 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)" : "none",
										}}
										priority={index === 0}
										fetchPriority={index === 0 ? "high" : "low"}
										loading={index === 0 ? "eager" : "lazy"}
										sizes="(max-width: 768px) 150px, (max-width: 1024px) 180px, 200px"
										quality={85}
									/>
								</div>
								<span
									className="text-lg md:text-2xl lg:text-3xl font-semibold text-center text-foreground/90 group-hover/card:font-bold transition-all duration-400"
									style={{
										transition: shouldAnimate ? "color 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)" : "none",
									}}
								>
									{slide.label}
								</span>
							</div>
						</div>
					</div>
				</div>
			))}

			<style jsx global>{`
				@keyframes gradient-shift {
					0%,
					100% {
						background-position: 0% 50%;
					}
					50% {
						background-position: 100% 50%;
					}
				}
			`}</style>
		</div>
	);
};
